import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Tooltip, Table, IconButton, Box, Flex } from '@radix-ui/themes';

import { fetchPopulationSegments } from '../../../lib/redux/actions.jsx';
import { useGetBoardSettings } from '../hooks/useGetBoardSettings';
import { usePlans } from '../../../lib/hooks/usePlans';

import Button from '../../../shared/Button';
import { ButtonGroup } from './StyledComponents';

import { TopBar } from '../../clientAdminUsers/components/shared/TopBar.jsx';
import { DeleteModal } from './Audiences/DeleteModal.jsx';
import { EditModal } from './Audiences/EditModal.jsx';
import { AudienceSummary } from './Audiences/AudienceSummary.jsx';
import Card from '../../../shared/Card/Card.jsx';
import { PlusIcon } from '../shared/Icons';
import styles from './Audiences.module.css';

const ConditionalTooltip = ({ condition, content, children }) => {
  return condition ? <Tooltip content={content}>{children}</Tooltip> : children;
};

export const Audiences = () => {
  const { audiences } = useGetBoardSettings();
  const { plans } = usePlans();
  const dispatch = useDispatch();
  const { t } = useTranslation('edit');
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [item, setItem] = useState(null);

  useEffect(() => {
    dispatch(fetchPopulationSegments());
  });

  return (
    <>
      <TopBar>
        <ConditionalTooltip
          condition={!plans?.audienceInfo.canAddAudience}
          content={t('audiencesList.upgrade')}
        >
          <ButtonGroup>
            <Button
              height="42px"
              variant="primary"
              disabled={!plans?.audienceInfo.canAddAudience}
              mode="outline"
              onClick={() => setOpenEditModal(true)}
            >
              {t('audienceForm.create.title')}
              <PlusIcon />
            </Button>
          </ButtonGroup>
        </ConditionalTooltip>
      </TopBar>
      <Box>
        <Card title={t('audiencesList.title')}>
          <Box
            p="4"
            style={{
              borderRadius: 'var(--radius-3)',
            }}
          >
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>
                    {t('audiencesList.name')}
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    {t('audiencesList.segments')}
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    {t('audiencesList.size')}
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {audiences.map(audience => (
                  <Table.Row key={audience.value} align="center">
                    <Table.Cell>
                      <Flex align="center" gap="2">
                        {audience.color && (
                          <Box
                            className={styles.colorDot}
                            style={{ backgroundColor: audience.color }}
                          />
                        )}
                        <Box>{audience.label}</Box>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <Box className={styles.audienceSummary}>
                        <AudienceSummary audience={audience} />
                      </Box>
                    </Table.Cell>
                    <Table.Cell>
                      {audience.audienceSize?.toLocaleString() ?? '-'}
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap="1" align="center">
                        <Tooltip content="Edit">
                          <IconButton
                            onClick={() => {
                              setItem(audience);
                              setOpenEditModal(true);
                            }}
                            color="gray"
                            variant="outline"
                            size="2"
                          >
                            <i className="fa-light fa-pencil" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip content="Delete">
                          <IconButton
                            onClick={() => {
                              setItem(audience);
                              setOpenDeleteModal(true);
                            }}
                            color="gray"
                            variant="outline"
                            size="2"
                          >
                            <i className="fa-light fa-trash-can" />
                          </IconButton>
                        </Tooltip>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Card>
      </Box>
      <DeleteModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        item={item}
        setItem={setItem}
      />
      <EditModal
        open={openEditModal}
        setOpen={setOpenEditModal}
        item={item}
        setItem={setItem}
      />
    </>
  );
};
